import { Accordion, Center, Text, Container, useMantineTheme, CopyButton, Tooltip, ActionIcon, rem, Badge, Loader, Indicator, Divider, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import classes from './OrderList.module.css';
import { useSession } from "../../auth/SessionContext";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { format, parseISO } from 'date-fns';
import { DEBUG } from "../..";
import { AppRefData, RefKeyMonthly, LoadRefKeyDataAsync, RefKeys } from './RefReport';
import { Features, hasFlag, listApps, ListAppsNames } from "../../scripts/FeatureCheck";
import { AppFlag } from "../../Settings";

export interface OrderData {
  id: number;
  ident: string;
  owner: string;
  discordId: string | null;
  created: string;
  features: number;
  refunded: boolean;
  steamId: number;
  strSteamId: string;
  steamIdBindingTime: string;
  affiliate: string;
}

type OrderDetailsProps = {
  data: OrderData;
  index: number;
};

export async function LoadOrderDataAsync(): Promise<OrderData[]>{
  return fetch('dyn/get-orders.php', {
    method: 'GET',
    credentials: 'include' // Dies sendet die Cookies mit
  })
  .then(response => response.json())
  .catch(error => { if(DEBUG) console.error('Error:', error) });
}
function formatDate(dateString : string): string {
  try {
    const date = parseISO(dateString);
    return format(date, 'dd.MM.yyyy HH:mm');
  } catch {
    return dateString; // Return original string if parsing fails
  }
}
const monthNames = [
  '', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

interface MonthlySummary {
  count: number;
  refunds: number;
  revenue: number;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({data, index}) => {
  const { t } = useTranslation("translation");
  return (
  <Accordion.Item value={`item-${index}`}>
    <Accordion.Control className={data.refunded ? classes.refunded : ''}><Indicator inline color="gray"  className={classes.badge} label={t("orders.header.creationDate")} position="middle-end" size={16}>{formatDate(data.created)}</Indicator></Accordion.Control>
    <Accordion.Panel className={data.refunded ? classes.refunded : ''}>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 0.9fr', gap: '8px', alignItems: 'baseline', alignContent: 'stretch', gridAutoRows: 'minmax(auto, max-content)' }}>
        <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.owner")}:</strong></div>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>{data.owner}</div>
        <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.features")}:</strong></div>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>{ListAppsNames(data.features).join(", ")}</div>
        <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.key")}:</strong></div>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>{data.ident}
          <CopyButton value={data.ident} timeout={2000}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="right">
                <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy}>
                  {copied ? (
                    <IconCheck style={{ width: rem(16) }} />
                  ) : (
                    <IconCopy style={{ width: rem(16) }} />
                  )}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </div>
        <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.activatedTo")}:</strong></div>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>{data.strSteamId}</div>
        <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.afiliate")}:</strong></div>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>{data.affiliate}</div>
      </div>
    </Accordion.Panel>
  </Accordion.Item>
  );
};


export function OrderList() {
  const navigate = useNavigate();

  const { t } = useTranslation("translation");
  const handleRedirect = () => {
    navigate('/');
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);  
  const [orderData, setOrderData] = useState<OrderData[]>([]);
  const [refData, setRefData] = useState<AppRefData>({});
  const { sessionData, loadSessionDataAsync, deleteSessionAsync, setSessionData } = useSession();
  const theme = useMantineTheme();
  useEffect(() => {
    const fetchData = async () => {
      try {
        var result = await loadSessionDataAsync();
        if(DEBUG) console.log("Imported sessionData in OrderList: ", result);
        if(!result) {
          handleRedirect();
        } else {
          const orderData = await LoadOrderDataAsync();
          LoadRefKeyDataAsync().then(setRefData);

          if(DEBUG) console.log("Order Data: ", orderData);
          const acadOrderData = orderData.filter(od => hasFlag(od.features, AppFlag));
          if(DEBUG) console.log("Order Data: ", acadOrderData);
          setOrderData(acadOrderData);
          setLoading(false);
        }
      } catch (err) {
        if (err instanceof Error) {
          if(DEBUG) console.error('Error:', err.message);
          setError(err.message);
        } else {
          if(DEBUG) console.error('Unknown error:', err);
          setError('An unknown error occurred');
        }
        setLoading(false);
      }
    };
    
    if(process.env.NODE_ENV !== 'development')
      fetchData();
    else{
      setOrderData([]);
      setLoading(false);
    }
  }, []);



  if (loading) {
    return (
      <Center style={{ height: '100vh' }}>
        <div>
          <Loader color="var(--mantine-color-yellow-filled)" size="xl" />
          <Text size="xl" mt="md">Loading...</Text>
        </div>
      </Center>
    );
  }
  const groupByYearMonth = (): Record<number, Record<number, { appName: string; data: RefKeyMonthly }[]>> => {
    const grouped: Record<number, Record<number, { appName: string; data: RefKeyMonthly }[]>> = {};

    for (const [appName, entries] of Object.entries(refData)) {
      for (const entry of entries) {
        const { year, month } = entry;
        if (!grouped[year]) grouped[year] = {};
        if (!grouped[year][month]) grouped[year][month] = [];
        grouped[year][month].push({ appName, data: entry });
      }
    }
    return grouped;
  };
  const grouped = groupByYearMonth();
  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Container size="100%" style={{ maxWidth: theme.breakpoints.lg, width: '100%' }}>
      <Accordion multiple variant="separated" defaultValue={['item-0']}>
        <Accordion.Item key={`orderList`} value={`orderList`}>
          <Accordion.Control>
            Your Keys
          </Accordion.Control>
          <Accordion.Panel>
            <Accordion multiple variant="separated" defaultValue={['item-0']}>
              {orderData.map((data, index) => (
                <Accordion.Item value={`item-${index}`}>
                  <Accordion.Control className={data.refunded ? classes.refunded : ''}><Indicator inline color="gray"  className={classes.badge} label={t("orders.header.creationDate")} position="middle-end" size={16}>{formatDate(data.created)}</Indicator></Accordion.Control>
                  <Accordion.Panel className={data.refunded ? classes.refunded : ''}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 0.9fr', gap: '8px', alignItems: 'baseline', alignContent: 'stretch', gridAutoRows: 'minmax(auto, max-content)' }}>
                      <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.owner")}:</strong></div>
                      <div style={{ display: 'flex', alignItems: 'stretch' }}>{data.owner}</div>
                      <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.key")}:</strong></div>
                      <div style={{ display: 'flex', alignItems: 'stretch' }}>{data.ident}
                        <CopyButton value={data.ident} timeout={2000}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="right">
                            <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy}>
                              {copied ? (
                                <IconCheck style={{ width: rem(16) }} />
                              ) : (
                                <IconCopy style={{ width: rem(16) }} />
                              )}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.activatedTo")}:</strong></div>
                      <div style={{ display: 'flex', alignItems: 'stretch' }}>{data.strSteamId}</div>
                      <div style={{ display: 'flex', alignItems: 'stretch' }}><strong>{t("orders.header.afiliate")}:</strong></div>
                      <div style={{ display: 'flex', alignItems: 'stretch' }}>{data.affiliate}</div>
                    </div>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Accordion.Panel>
        </Accordion.Item>
        
        <Accordion.Item key={`acciliateReport`} value={`affiliateReport`}>
          <Accordion.Control>
            Affiliate Report
          </Accordion.Control>
          <Accordion.Panel>
            <Accordion multiple variant="separated">
              {Object.entries(grouped).sort(([a], [b]) => +b - +a).map(([year, months]) => {
                const yearSummary = Object.values(months).flat().reduce((acc, { data }) => {
                  data.refKeys.forEach(rk => {
                    acc.count += rk.count;
                    acc.refunds += rk.refunds;
                    acc.revenue += rk.revenue * rk.share;
                  });
                  return acc;
                }, { count: 0, refunds: 0, revenue: 0 });

                return (
                  <Accordion.Item key={`year-${year}`} value={`year-${year}`}>
                    <Accordion.Control>
                      Jahr {year} – {yearSummary.revenue.toFixed(2)} € - {yearSummary.count} Keys
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Accordion multiple variant="separated">
                        {Object.entries(months).sort(([a], [b]) => +b - +a).map(([month, entries]) => {
                          const monthSummary = entries.flatMap(e => e.data.refKeys).reduce((acc, rk) => {
                            acc.count += rk.count;
                            acc.refunds += rk.refunds;
                            acc.revenue += rk.revenue * rk.share;
                            return acc;
                          }, { count: 0, refunds: 0, revenue: 0 });

                          return (
                            <Accordion.Item key={`month-${year}-${month}`} value={`month-${year}-${month}`}>
                              <Accordion.Control>
                                {monthNames[+month]} {year} – {monthSummary.revenue.toFixed(2)} € - {monthSummary.count} Keys
                              </Accordion.Control>
                              <Accordion.Panel>
                                <div>
                                  {Object.entries(
                                    entries.reduce((acc, { appName, data }) => {
                                      if (!acc[appName]) acc[appName] = [];
                                      acc[appName].push(...data.refKeys);
                                      return acc;
                                    }, {} as Record<string, RefKeys[]>)
                                  ).map(([appName, refKeys]) => {
                                    const total = refKeys.reduce((acc, rk) => {
                                      acc.count += rk.count;
                                      acc.refunds += rk.refunds;
                                      acc.revenue += rk.revenue * rk.share;
                                      return acc;
                                    }, { count: 0, refunds: 0, revenue: 0 });

                                    return (
                                      <div key={appName} style={{ marginBottom: 12, borderBottom: '1px solid #eee', paddingBottom: 6 }}>
                                        <strong>{appName}</strong><br />
                                        Keys: {total.count}, Refunds: {total.refunds}, Revenue: {total.revenue.toFixed(2)} €

                                        <div style={{ marginTop: 6, marginLeft: 12 }}>
                                          {refKeys.map((rk, i) => (
                                            <div key={i} style={{ fontSize: '0.9em', padding: '2px 0' }}>
                                              <strong>{rk.refName}</strong>: {rk.count} Keys, {rk.refunds} Refunds, {(rk.revenue * rk.share).toFixed(2)} €
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </Accordion.Panel>
                            </Accordion.Item>
                          );
                        })}
                      </Accordion>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Container>
  );
}
